import { useMemo } from "react";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import type { MarketingCampaignBudgetSegment } from "@common/interfaces/KYDMarketing";
import type { PhaseCalendarNeighborBounds } from "../components/GuidedBudgetEditor";
import {
  META_PHASE_MIN_GAP_MINUTES,
  type ScheduledBudgetAdDeliveryWindowUtc,
  wallMinuteToDayjsInEventTimezone,
} from "../utils/eventWallTime";
import type { FieldErrors } from "react-hook-form";
import type {
  BudgetData,
  BudgetPhaseForm,
} from "../components/GuidedBudgetEditor/BudgetSelector";
import { NO_SCHEDULED_SEGMENTS } from "../components/GuidedBudgetEditor/guidedBudgetEditorTokens";
import { sumPhaseAmounts } from "../components/GuidedBudgetEditor/guidedBudgetEditorMoney";
import {
  createScheduledBudgetEditPolicy,
  type ScheduledBudgetEditPolicy,
} from "../utils/scheduledBudgetEditPolicy";
import { getScheduledBudgetPhaseTimelineError } from "../utils/scheduledBudgetPhaseTimelineValidation";
import {
  flattenScheduledBudgetFormErrors,
  scheduledBudgetSegmentsArrayMessage,
} from "../utils/scheduledBudgetRhfErrors";
import { scheduledBudgetTimelineImplicitWalls } from "../utils/validateScheduledBudgetForm";

dayjs.extend(utc);
dayjs.extend(timezone);

type UseGuidedBudgetScheduledArgs = {
  isScheduled: boolean;
  budget?: BudgetData;
  eventTimezone: string;
  /** Full ad delivery window (campaign run start/end in UTC ISO). */
  scheduledBudgetDeliveryWindow: ScheduledBudgetAdDeliveryWindowUtc | null;
  /**
   * When set (edit budget modal + Yup/RHF), phased timeline copy comes from form errors.
   * When omitted (create flow), timeline is validated locally.
   */
  scheduledBudgetFormErrors?: FieldErrors<BudgetData>;
  /**
   * Campaign is synced to Meta (`external_id`): ended phases read-only, start capped at now.
   */
  linkedToMeta?: boolean;
};

export function useGuidedBudgetScheduled({
  isScheduled,
  budget,
  eventTimezone,
  scheduledBudgetDeliveryWindow,
  scheduledBudgetFormErrors,
  linkedToMeta = false,
}: UseGuidedBudgetScheduledArgs) {
  const scheduledSegments: BudgetPhaseForm[] = useMemo(
    () => budget?.segments ?? NO_SCHEDULED_SEGMENTS,
    [budget?.segments]
  );

  const scheduledBudgetEditPolicy: ScheduledBudgetEditPolicy = useMemo(
    () =>
      createScheduledBudgetEditPolicy({
        linkedToMeta,
        scheduledBudget:
          linkedToMeta && isScheduled && budget?.type === "scheduled"
            ? {
                type: "scheduled" as const,
                segments: (budget.segments ??
                  NO_SCHEDULED_SEGMENTS) as MarketingCampaignBudgetSegment[],
              }
            : undefined,
        eventTimezone,
      }),
    [linkedToMeta, isScheduled, budget, eventTimezone]
  );

  const scheduledTotal = useMemo(
    () => sumPhaseAmounts(scheduledSegments),
    [scheduledSegments]
  );

  const usesYupFormErrors = scheduledBudgetFormErrors !== undefined;
  const implicitDeliveryWindowWalls = useMemo(
    () =>
      scheduledBudgetTimelineImplicitWalls(
        eventTimezone,
        scheduledBudgetDeliveryWindow
      ),
    [scheduledBudgetDeliveryWindow, eventTimezone]
  );

  const deliveryWindowInEventTimezone = useMemo((): {
    start: Dayjs;
    end: Dayjs;
  } | null => {
    const fs = scheduledBudgetDeliveryWindow?.deliveryWindowStartIso?.trim();
    const fe = scheduledBudgetDeliveryWindow?.deliveryWindowEndIso?.trim();
    if (!fs || !fe) return null;
    const tz = eventTimezone.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
    const start = dayjs(fs).tz(tz);
    const end = dayjs(fe).tz(tz);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;
    return { start, end };
  }, [scheduledBudgetDeliveryWindow, eventTimezone]);

  const internalScheduledPhaseTimelineError = useMemo(
    () =>
      usesYupFormErrors
        ? null
        : isScheduled
          ? getScheduledBudgetPhaseTimelineError(
              scheduledSegments,
              eventTimezone,
              implicitDeliveryWindowWalls
            )
          : null,
    [
      usesYupFormErrors,
      isScheduled,
      scheduledSegments,
      eventTimezone,
      implicitDeliveryWindowWalls,
    ]
  );

  const scheduledPhaseTimelineError = usesYupFormErrors
    ? flattenScheduledBudgetFormErrors(scheduledBudgetFormErrors) ??
      scheduledBudgetSegmentsArrayMessage(scheduledBudgetFormErrors) ??
      null
    : internalScheduledPhaseTimelineError;

  /** Keeps each phase’s pickers from crossing the previous / next segment (contiguous chain). */
  const phaseNeighborPickerBounds = useMemo((): Array<
    PhaseCalendarNeighborBounds | null
  > => {
    if (!isScheduled) return [];
    const tz = eventTimezone.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
    return scheduledSegments.map((_, index) => {
      const prev = index > 0 ? scheduledSegments[index - 1] : undefined;
      const next =
        index + 1 < scheduledSegments.length
          ? scheduledSegments[index + 1]
          : undefined;
      let startMin: Dayjs | undefined;
      if (prev?.time_end?.trim()) {
        const d = wallMinuteToDayjsInEventTimezone(prev.time_end, tz);
        if (d?.isValid()) startMin = d;
      }
      let endMax: Dayjs | undefined;
      if (next?.time_end?.trim()) {
        const dEnd = wallMinuteToDayjsInEventTimezone(next.time_end, tz);
        if (dEnd?.isValid()) {
          const capped = dEnd.subtract(META_PHASE_MIN_GAP_MINUTES, "minute");
          if (capped.isValid()) endMax = capped;
        }
      }
      if (startMin == null && endMax == null) return null;
      const o: PhaseCalendarNeighborBounds = {};
      if (startMin) o.startMin = startMin;
      if (endMax) o.endMax = endMax;
      return o;
    });
  }, [isScheduled, scheduledSegments, eventTimezone]);

  return {
    scheduledSegments,
    scheduledBudgetEditPolicy,
    scheduledTotal,
    deliveryWindowInEventTimezone,
    scheduledPhaseTimelineError,
    phaseNeighborPickerBounds,
  };
}
